import React from "react"
import { useTranslation } from "react-i18next"

import { Button, Checkbox, Pill } from "components"
import { thousandSeparator } from "utils"
import classes from "./FoodModalOption.module.scss"

const FoodModalOption = props => {
  const {
    form,
    setForm,
    specification,
    specificationItem,
    maxSelect
  } = props

  const [t] = useTranslation()

  const getEffectiveMaxQuantity = () => {
    if (Number(maxSelect) > 0) return Number(maxSelect)

    if (specification.maxQuantity === 0) return undefined

    return specification.maxQuantity
  }

  const getSelectedSpecificationQuantity = (
    items = form.specificationItems
  ) =>
    items.reduce((sum, selectedSpecificationItem) => {
      if (
        String(specification._id) !==
        String(selectedSpecificationItem.specificationId)
      ) {
        return sum
      }

      return sum + (selectedSpecificationItem.quantity || 1)
    }, 0)

  const canAddSpecificationItem = () => {
    const maxQuantity =
      getEffectiveMaxQuantity()

    if (maxQuantity === undefined) return true

    return getSelectedSpecificationQuantity() < maxQuantity
  }

  const setSelectedSpecificationItemsFreeQuantity = (newSelectedSpecificationItems) => {
    let remainingFreeQuantity =
      specification.maxFreeQuantity

    const result =
      newSelectedSpecificationItems.map(selectedSpecificationItem => {
        if (
          String(selectedSpecificationItem.specificationId) !==
          String(specification._id)
        ) {
          return selectedSpecificationItem
        }

        const freeQuantity =
          Math.min(remainingFreeQuantity, selectedSpecificationItem.quantity)

        remainingFreeQuantity -= freeQuantity

        return {
          ...selectedSpecificationItem,
          freeQuantity
        }
      })


    return result
  }

  const handleRemoveItem = () => {
    setForm(prev => {
      const newSelectedSpecificationItems =
        prev.specificationItems.filter(si =>
          si._id !== specificationItem._id
        )

      const selectedSpecificationItemWithFreeQuantity =
        setSelectedSpecificationItemsFreeQuantity(
          newSelectedSpecificationItems
        )

      return {
        ...prev,
        specificationItems:
          selectedSpecificationItemWithFreeQuantity
      }
    })
  }

  const createSelectedSpecificationItem = () => ({
    ...specificationItem,
    quantity: 1,
    freeQuantity: 0,
    specificationId: specification._id,
    specificationMaxFreeQuantity: specification.maxFreeQuantity
  })

  const handleAddItem = () => {
    setForm(prev => {
      const maxQuantity =
        getEffectiveMaxQuantity()

      if (
        maxQuantity !== undefined &&
        getSelectedSpecificationQuantity(prev.specificationItems) >= maxQuantity
      ) {
        return prev
      }

      const newSelectedSpecificationItems =
        [...prev.specificationItems,
        createSelectedSpecificationItem()]

      const selectedSpecificationItemWithFreeQuantity =
        setSelectedSpecificationItemsFreeQuantity(
          newSelectedSpecificationItems
        )

      return {
        ...prev,
        specificationItems:
          selectedSpecificationItemWithFreeQuantity
      }
    })
  }

  const handleChangeItem = (value) => {
    if (value === 0) return handleRemoveItem()

    setForm(prev => {
      const maxQuantity =
        getEffectiveMaxQuantity()

      const newSelectedSpecificationItems =
        prev.specificationItems.map(si =>
          si._id === specificationItem._id
            ? { ...si, quantity: value }
            : si
        )

      if (
        maxQuantity !== undefined &&
        getSelectedSpecificationQuantity(newSelectedSpecificationItems) > maxQuantity
      ) {
        return prev
      }

      const selectedSpecificationItemWithFreeQuantity =
        setSelectedSpecificationItemsFreeQuantity(
          newSelectedSpecificationItems
        )

      return {
        ...prev,
        specificationItems:
          selectedSpecificationItemWithFreeQuantity
      }
    })
  }

  const selectedSpecificationItem =
    form.specificationItems.find(si =>
      si._id === specificationItem._id
    )

  const addDisabled =
    !specificationItem.isAvailable ||
    !canAddSpecificationItem()

  const effectiveMaxQuantity =
    getEffectiveMaxQuantity()

  const selectedSpecificationQuantity =
    getSelectedSpecificationQuantity()

  const remainingQuantity =
    effectiveMaxQuantity === undefined
      ? undefined
      : Math.max(effectiveMaxQuantity - selectedSpecificationQuantity, 0)

  const selectedItemMaxQuantity =
    effectiveMaxQuantity === undefined
      ? undefined
      : (selectedSpecificationItem?.quantity || 0) + remainingQuantity

  return (
    <div className={classes["option"]}>
      <div className={classes["option-details"]}>
        <div className={classes["name"]}>
          {specificationItem.name}
        </div>

        {
          specificationItem.salePrice > 0 &&
          <div className={classes["price"]}>
            {thousandSeparator(specificationItem.salePrice)}
          </div>
        }
      </div>

      {specificationItem.isSelectable
        ?
        <Checkbox
          disabled={!selectedSpecificationItem && addDisabled}
          onChange={(checked) => checked ? handleAddItem() : handleRemoveItem()}
          checked={!!selectedSpecificationItem}
        />
        :
        selectedSpecificationItem
          ?
          <Pill
            max={selectedItemMaxQuantity || 1000}
            onChange={handleChangeItem}
            value={selectedSpecificationItem.quantity}
            className={classes["pill"]}
          />
          :
          <Button
            disabled={addDisabled}
            className={classes["button"]}
            onClick={handleAddItem}
          >
            {t("افزودن")}
          </Button>
      }
    </div>
  )
}

export default FoodModalOption
